import type {
  InternalCompany,
  ClientOrganization,
  ProfileType,
  WorkerOrganization,
  WorkerProfile,
  Lob,
  TemplateV2,
  Placement,
} from "./types"
import {
  DECISION_TABLES,
  getActiveLobs,
  isStatusBlocked as isStatusBlockedFn,
} from "./decision-tables"

// ---------------------------------------------------------------------------
// Reference data
// ---------------------------------------------------------------------------

export const internalCompanies: InternalCompany[] = [
  { id: "ic-001", name: "Procom Canada" },
  { id: "ic-002", name: "Procom US" },
  { id: "ic-003", name: "Procom EMEA" },
]

export const clientOrganizations: ClientOrganization[] = [
  { id: "cl-001", name: "Northwind Retail" },
  { id: "cl-002", name: "Contoso Health" },
  { id: "cl-003", name: "Fabrikam Manufacturing" },
  { id: "cl-004", name: "Globex Telecom" },
  { id: "cl-005", name: "Initech Financial" },
]

// ---------------------------------------------------------------------------
// STRICT Profile Types -- derived from DECISION_TABLES
// ---------------------------------------------------------------------------
export const profileTypes: ProfileType[] = DECISION_TABLES.PROFILE_TYPES.map(
  (pt) => ({
    id: pt.id,
    code: pt.code,
    value: pt.value,
    orgRequired: DECISION_TABLES.WORKER_ORG.REQUIRED_FOR.has(pt.code),
  })
)

// ---------------------------------------------------------------------------
// STRICT Lines of Business -- derived from DECISION_TABLES (isDeleted=0 only)
// ---------------------------------------------------------------------------
export const lobs: Lob[] = getActiveLobs().map((l) => ({
  id: l.id,
  code: l.code,
  value: l.value,
}))

export const workerOrganizations: WorkerOrganization[] = [
  { id: "wo-001", name: "Bennett Consulting Inc." },
  { id: "wo-002", name: "Park Solutions Ltd." },
  { id: "wo-003", name: "Chen Digital Services" },
  { id: "wo-004", name: "Rivera Contracting" },
  // Used by mock correction flow (?mockResult=correction) — simulates an org created on the server
  { id: "900", name: "Mock Created Organization" },
]

// ---------------------------------------------------------------------------
// Worker profiles -- uses profile type CODE for matching
// ---------------------------------------------------------------------------
export const workerProfiles: WorkerProfile[] = [
  // Aisha Khan -- T4 Worker, Active
  { id: "wk-001", name: "Aisha Khan",       email: "aisha.khan@example.com",       profileTypeCode: "WorkerTemp",        workerOrgId: null,     contactId: "ct-001", contactStatus: "Active", profileStatus: "Active" },

  // Daniel Park -- Inc (with org) + T4 (no org) + W2 (no org), Active
  { id: "wk-002", name: "Daniel Park",      email: "daniel.park@example.com",      profileTypeCode: "WorkerCanadianInc", workerOrgId: "wo-002", contactId: "ct-002", contactStatus: "Active", profileStatus: "Active" },
  { id: "wk-007", name: "Daniel Park",      email: "daniel.park@example.com",      profileTypeCode: "WorkerTemp",        workerOrgId: null,     contactId: "ct-002", contactStatus: "Active", profileStatus: "Active" },
  { id: "wk-010", name: "Daniel Park",      email: "daniel.park@example.com",      profileTypeCode: "WorkerUnitedStatesW2", workerOrgId: null,  contactId: "ct-002", contactStatus: "Active", profileStatus: "Active" },

  // Mei Chen -- Subvendor (with org) + Canadian SP (no org), Active
  { id: "wk-003", name: "Mei Chen",         email: "mei.chen@example.com",         profileTypeCode: "WorkerSubVendor",   workerOrgId: "wo-003", contactId: "ct-003", contactStatus: "Active", profileStatus: "Active" },
  { id: "wk-011", name: "Mei Chen",         email: "mei.chen@example.com",         profileTypeCode: "WorkerCanadianSP",  workerOrgId: null,     contactId: "ct-003", contactStatus: "Active", profileStatus: "Active" },

  // Santiago Rivera -- T4 + Inc (with org), PendingChange status
  { id: "wk-004", name: "Santiago Rivera",  email: "santiago.rivera@example.com",  profileTypeCode: "WorkerTemp",        workerOrgId: null,     contactId: "ct-004", contactStatus: "PendingChange", profileStatus: "PendingChange" },
  { id: "wk-012", name: "Santiago Rivera",  email: "santiago.rivera@example.com",  profileTypeCode: "WorkerCanadianInc", workerOrgId: "wo-004", contactId: "ct-004", contactStatus: "PendingChange", profileStatus: "PendingChange" },

  // Priya Nair -- Canadian SP only, Active
  { id: "wk-005", name: "Priya Nair",       email: "priya.nair@example.com",       profileTypeCode: "WorkerCanadianSP",  workerOrgId: null,     contactId: "ct-005", contactStatus: "Active", profileStatus: "Active" },

  // Noah Bennett -- Inc (with org) + T4 (no org), Active
  { id: "wk-006", name: "Noah Bennett",     email: "noah.bennett@example.com",     profileTypeCode: "WorkerCanadianInc", workerOrgId: "wo-001", contactId: "ct-006", contactStatus: "Active", profileStatus: "Active" },
  { id: "wk-008", name: "Noah Bennett",     email: "noah.bennett@example.com",     profileTypeCode: "WorkerTemp",        workerOrgId: null,     contactId: "ct-006", contactStatus: "Active", profileStatus: "Active" },

  // Multi-contact scenario: same email, two different contacts
  // Morgan Lee -- Contact 101 has T4 + Canadian SP; Contact 102 has Inc (with org)
  { id: "wk-101", name: "Morgan Lee",       email: "morgan.lee@example.com",       profileTypeCode: "WorkerTemp",        workerOrgId: null,     contactId: "ct-101", contactStatus: "Active", profileStatus: "Active" },
  { id: "wk-102", name: "Morgan Lee",       email: "morgan.lee@example.com",       profileTypeCode: "WorkerCanadianSP",  workerOrgId: null,     contactId: "ct-101", contactStatus: "Active", profileStatus: "Active" },
  { id: "wk-103", name: "Morgan Lee",       email: "morgan.lee@example.com",       profileTypeCode: "WorkerCanadianInc", workerOrgId: "wo-002", contactId: "ct-102", contactStatus: "Active", profileStatus: "Active" },
]

// ---------------------------------------------------------------------------
// Mock runtime: entities "created" by mock correction flow (?mockResult=correction).
// Populated by the mock submit handler; consumed by mock hooks on re-fetch.
// Resets on page reload (module-level state).
// ---------------------------------------------------------------------------
export const mockCreatedEntities = {
  profiles: [] as WorkerProfile[],
  organizations: [] as WorkerOrganization[],
}

// ---------------------------------------------------------------------------
// Templates (each belongs to exactly one LOB + one client org)
// cl-005 (Initech Financial) has no templates → "No templates available" state.
// ---------------------------------------------------------------------------
export const templatesV2: TemplateV2[] = [
  { id: "tpl-001", name: "Regular - Standard SOW",         lobCode: "Regular",            clientOrgId: "cl-001" },
  { id: "tpl-002", name: "Regular - Security Addendum",    lobCode: "Regular",            clientOrgId: "cl-002" },
  { id: "tpl-003", name: "Payroll - Standard",             lobCode: "Payroll",            clientOrgId: "cl-001" },
  { id: "tpl-004", name: "Payroll - Overtime Addendum",    lobCode: "Payroll",            clientOrgId: "cl-003" },
  { id: "tpl-005", name: "Subvendor Payroll - Standard",   lobCode: "SubVendorPayroll",   clientOrgId: "cl-002" },
  { id: "tpl-006", name: "Subvendor Payroll - Compliance", lobCode: "SubVendorPayroll",   clientOrgId: "cl-004" },
  { id: "tpl-007", name: "Perm Placement - Standard",      lobCode: "PermPlacement",      clientOrgId: "cl-001" },
  { id: "tpl-008", name: "Expense - Standard",             lobCode: "Expense",            clientOrgId: "cl-002" },
  { id: "tpl-009", name: "Subvendor Placement - Standard", lobCode: "SubVendorPlacement", clientOrgId: "cl-003" },
  { id: "tpl-010", name: "SOW - Standard",                 lobCode: "StatementOfWork",    clientOrgId: "cl-001" },
  { id: "tpl-011", name: "Direct Sourcing - Standard",     lobCode: "DirectSourcing",     clientOrgId: "cl-004" },
]

// ---------------------------------------------------------------------------
// Pending-only placements (V1.2: only pending, never started)
// ---------------------------------------------------------------------------
export const initialPlacements: Placement[] = [
  {
    id: "ATS-10001",
    atsId: "ATS-10001",
    internalCompanyId: "ic-001",
    clientOrgId: "cl-001",
    workerId: "wk-001",
    workerContactId: null,
    workerOrgId: null,
    workerEmail: "aisha.khan@example.com",
    lobCode: "Payroll",
    profileTypeCode: "WorkerTemp",
    startDate: "2026-03-01",
    endDate: "2026-08-31",
    billRate: 95,
    payRate: 62,
    billCurrencyCode: "CAD",
    payCurrencyCode: "CAD",
    templateV2Id: null,
    dateCreated: "2026-01-10",
    status: "Pending",
    atsSource: {
      workerName: "Aisha Khan",
      workerEmail: "aisha.khan@example.com",
      clientOrgName: "Northwind Retail",
      internalCompanyName: "Procom Canada",
      profileTypeCode: "WorkerTemp",
      workerOrgName: null,
      lobCode: "Payroll",
      startDate: "2026-03-01",
      endDate: "2026-08-31",
      billRate: 95,
      payRate: 62,
      billCurrencyCode: "CAD",
      payCurrencyCode: "CAD",
    },
  },
  {
    id: "ATS-10003",
    atsId: "ATS-10003",
    internalCompanyId: "ic-002",
    clientOrgId: "cl-003",
    workerId: "wk-003",
    workerContactId: null,
    workerOrgId: "wo-003",
    workerEmail: "mei.chen@example.com",
    lobCode: null,
    profileTypeCode: "WorkerSubVendor",
    startDate: "2026-04-01",
    endDate: "2026-09-30",
    billRate: 125,
    payRate: 90,
    billCurrencyCode: "USD",
    payCurrencyCode: "USD",
    templateV2Id: null,
    dateCreated: "2026-01-18",
    status: "Pending",
    atsSource: {
      workerName: "Mei Chen",
      workerEmail: "mei.chen@example.com",
      clientOrgName: "Fabrikam Manufacturing",
      internalCompanyName: "Procom US",
      profileTypeCode: "WorkerSubVendor",
      workerOrgName: "Chen Digital Services",
      lobCode: null,
      startDate: "2026-04-01",
      endDate: "2026-09-30",
      billRate: 125,
      payRate: 90,
      billCurrencyCode: "USD",
      payCurrencyCode: "USD",
    },
  },
  {
    id: "ATS-10004",
    atsId: "ATS-10004",
    internalCompanyId: "ic-002",
    clientOrgId: "cl-004",
    workerId: "wk-004",
    workerContactId: null,
    workerOrgId: null,
    workerEmail: "santiago.rivera@example.com",
    lobCode: "Regular",
    profileTypeCode: "WorkerTemp",
    startDate: "2026-03-15",
    endDate: "2026-09-15",
    billRate: 88,
    payRate: 55,
    billCurrencyCode: "CAD",
    payCurrencyCode: "CAD",
    templateV2Id: null,
    dateCreated: "2026-01-22",
    status: "Pending",
    atsSource: {
      workerName: "Santiago Rivera",
      workerEmail: "santiago.rivera@example.com",
      clientOrgName: "Globex Telecom",
      internalCompanyName: "Procom US",
      profileTypeCode: "WorkerTemp",
      workerOrgName: null,
      lobCode: "Regular",
      startDate: "2026-03-15",
      endDate: "2026-09-15",
      billRate: 88,
      payRate: 55,
      billCurrencyCode: "CAD",
      payCurrencyCode: "CAD",
    },
  },
  {
    id: "ATS-10006",
    atsId: "ATS-10006",
    internalCompanyId: "ic-003",
    clientOrgId: "cl-001",
    workerId: "wk-006",
    workerContactId: null,
    workerOrgId: "wo-001",
    workerEmail: "noah.bennett@example.com",
    lobCode: "Regular",
    profileTypeCode: "WorkerCanadianInc",
    startDate: "2026-05-01",
    endDate: "2026-10-31",
    billRate: 140,
    payRate: 100,
    billCurrencyCode: "USD",
    payCurrencyCode: "CAD",
    templateV2Id: null,
    dateCreated: "2026-01-28",
    status: "Pending",
    atsSource: {
      workerName: "Noah Bennett",
      workerEmail: "noah.bennett@example.com",
      clientOrgName: "Northwind Retail",
      internalCompanyName: "Procom EMEA",
      profileTypeCode: "WorkerCanadianInc",
      workerOrgName: "Bennett Consulting Inc.",
      lobCode: "Regular",
      startDate: "2026-05-01",
      endDate: "2026-10-31",
      billRate: 140,
      payRate: 100,
      billCurrencyCode: "USD",
      payCurrencyCode: "CAD",
    },
  },
  {
    id: "ATS-10007",
    atsId: "ATS-10007",
    internalCompanyId: "ic-001",
    clientOrgId: "cl-003",
    workerId: "wk-001",
    workerContactId: null,
    workerOrgId: null,
    workerEmail: "aisha.khan@example.com",
    lobCode: "Payroll",
    profileTypeCode: "WorkerTemp",
    startDate: "2026-06-01",
    endDate: "2026-11-30",
    billRate: 98,
    payRate: 65,
    billCurrencyCode: "CAD",
    payCurrencyCode: "CAD",
    templateV2Id: null,
    dateCreated: "2026-02-01",
    status: "Pending",
    atsSource: {
      workerName: "Aisha Khan",
      workerEmail: "aisha.khan@example.com",
      clientOrgName: "Fabrikam Manufacturing",
      internalCompanyName: "Procom Canada",
      profileTypeCode: "WorkerTemp",
      workerOrgName: null,
      lobCode: "Payroll",
      startDate: "2026-06-01",
      endDate: "2026-11-30",
      billRate: 98,
      payRate: 65,
      billCurrencyCode: "CAD",
      payCurrencyCode: "CAD",
    },
  },
  {
    id: "ATS-10008",
    atsId: "ATS-10008",
    internalCompanyId: "ic-002",
    clientOrgId: "cl-002",
    workerId: "wk-003",
    workerContactId: null,
    workerOrgId: "wo-003",
    workerEmail: "mei.chen@example.com",
    lobCode: "SubVendorPayroll",
    profileTypeCode: "WorkerSubVendor",
    startDate: "2026-03-10",
    endDate: "2026-08-10",
    billRate: 115,
    payRate: 82,
    billCurrencyCode: "CAD",
    payCurrencyCode: "CAD",
    templateV2Id: null,
    dateCreated: "2026-01-14",
    status: "Pending",
    atsSource: {
      workerName: "Mei Chen",
      workerEmail: "mei.chen@example.com",
      clientOrgName: "Contoso Health",
      internalCompanyName: "Procom US",
      profileTypeCode: "WorkerSubVendor",
      workerOrgName: "Chen Digital Services",
      lobCode: "SubVendorPayroll",
      startDate: "2026-03-10",
      endDate: "2026-08-10",
      billRate: 115,
      payRate: 82,
      billCurrencyCode: "CAD",
      payCurrencyCode: "CAD",
    },
  },
  {
    id: "ATS-10009",
    atsId: "ATS-10009",
    internalCompanyId: "ic-003",
    clientOrgId: "cl-004",
    workerId: "wk-006",
    workerContactId: null,
    workerOrgId: "wo-001",
    workerEmail: "noah.bennett@example.com",
    lobCode: "Regular",
    profileTypeCode: "WorkerCanadianInc",
    startDate: "2026-04-15",
    endDate: "2026-10-15",
    billRate: 130,
    payRate: 92,
    billCurrencyCode: "CAD",
    payCurrencyCode: "USD",
    templateV2Id: null,
    dateCreated: "2026-01-25",
    status: "Pending",
    atsSource: {
      workerName: "N. Bennett",
      workerEmail: "noah.bennett@example.com",
      clientOrgName: "Globex Telecom",
      internalCompanyName: "Procom EMEA",
      profileTypeCode: "WorkerCanadianInc",
      workerOrgName: "Bennett Consulting Inc.",
      lobCode: "Regular",
      startDate: "2026-04-15",
      endDate: "2026-10-15",
      billRate: 130,
      payRate: 92,
      billCurrencyCode: "CAD",
      payCurrencyCode: "USD",
    },
  },
  {
    id: "ATS-10010",
    atsId: "ATS-10010",
    internalCompanyId: "ic-001",
    clientOrgId: "cl-005",
    workerId: "wk-004",
    workerContactId: null,
    workerOrgId: null,
    workerEmail: "santiago.rivera@example.com",
    lobCode: "Payroll",
    profileTypeCode: "WorkerTemp",
    startDate: "2026-02-20",
    endDate: "2026-08-20",
    billRate: 85,
    payRate: 52,
    billCurrencyCode: "USD",
    payCurrencyCode: "USD",
    templateV2Id: null,
    dateCreated: "2026-01-08",
    status: "Pending",
    atsSource: {
      workerName: "Santiago Rivera",
      workerEmail: "santiago.rivera@example.com",
      clientOrgName: "Initech Financial",
      internalCompanyName: "Procom Canada",
      profileTypeCode: "WorkerTemp",
      workerOrgName: null,
      lobCode: "Payroll",
      startDate: "2026-02-20",
      endDate: "2026-08-20",
      billRate: 85,
      payRate: 52,
      billCurrencyCode: "USD",
      payCurrencyCode: "USD",
    },
  },
  {
    // Edge case: profile type is blank / not mapped from ATS
    id: "ATS-10012",
    atsId: "ATS-10012",
    internalCompanyId: "ic-001",
    clientOrgId: "cl-002",
    workerId: "wk-005",
    workerContactId: null,
    workerOrgId: null,
    workerEmail: "priya.nair@example.com",
    lobCode: "Regular",
    profileTypeCode: null,
    startDate: "2026-05-10",
    endDate: "2026-11-10",
    billRate: 78,
    payRate: 50,
    billCurrencyCode: null,
    payCurrencyCode: null,
    templateV2Id: null,
    dateCreated: "2026-02-05",
    status: "Pending",
    atsSource: {
      workerName: "Priya Nair",
      workerEmail: "priya.nair@example.com",
      clientOrgName: "Contoso Health",
      internalCompanyName: "Procom Canada",
      profileTypeCode: null,
      workerOrgName: null,
      lobCode: "Regular",
      startDate: "2026-05-10",
      endDate: "2026-11-10",
      billRate: 78,
      payRate: 50,
      billCurrencyCode: null,
      payCurrencyCode: null,
    },
  },
  {
    // Edge case: brand new worker -- email not in our system at all (NEW tag)
    id: "ATS-10013",
    atsId: "ATS-10013",
    internalCompanyId: "ic-002",
    clientOrgId: "cl-003",
    workerId: null,
    workerContactId: null,
    workerOrgId: null,
    workerEmail: "jordan.reeves@example.com",
    lobCode: "Regular",
    profileTypeCode: "WorkerTemp",
    startDate: "2026-06-15",
    endDate: "2026-12-15",
    billRate: 92,
    payRate: 60,
    billCurrencyCode: null,
    payCurrencyCode: null,
    templateV2Id: null,
    dateCreated: "2026-02-08",
    status: "Pending",
    atsSource: {
      workerName: "Jordan Reeves",
      workerEmail: "jordan.reeves@example.com",
      clientOrgName: "Fabrikam Manufacturing",
      internalCompanyName: "Procom US",
      profileTypeCode: "WorkerTemp",
      workerOrgName: null,
      lobCode: "Regular",
      startDate: "2026-06-15",
      endDate: "2026-12-15",
      billRate: 92,
      payRate: 60,
      billCurrencyCode: null,
      payCurrencyCode: null,
    },
  },
  {
    // Multi-contact: Morgan Lee has two contacts for the same email
    id: "ATS-10014",
    atsId: "ATS-10014",
    internalCompanyId: "ic-001",
    clientOrgId: "cl-002",
    workerId: null,
    workerContactId: null,
    workerOrgId: null,
    workerEmail: "morgan.lee@example.com",
    lobCode: "Regular",
    profileTypeCode: "WorkerTemp",
    startDate: "2026-07-01",
    endDate: "2026-12-31",
    billRate: 100,
    payRate: 70,
    billCurrencyCode: "CAD",
    payCurrencyCode: "CAD",
    templateV2Id: null,
    dateCreated: "2026-02-10",
    status: "Pending",
    atsSource: {
      workerName: "Morgan Lee",
      workerEmail: "morgan.lee@example.com",
      clientOrgName: "Contoso Health",
      internalCompanyName: "Procom Canada",
      profileTypeCode: "WorkerTemp",
      workerOrgName: null,
      lobCode: "Regular",
      startDate: "2026-07-01",
      endDate: "2026-12-31",
      billRate: 100,
      payRate: 70,
      billCurrencyCode: "CAD",
      payCurrencyCode: "CAD",
    },
  },
  {
    // Edge case: brand new contact + brand new org -- neither exist in FBO
    id: "ATS-10015",
    atsId: "ATS-10015",
    internalCompanyId: "ic-001",
    clientOrgId: "cl-002",
    workerId: null,
    workerContactId: null,
    workerOrgId: null,
    workerEmail: "taylor.novak@example.com",
    lobCode: "Regular",
    profileTypeCode: "WorkerCanadianInc",
    startDate: "2026-08-01",
    endDate: "2027-01-31",
    billRate: 135,
    payRate: 95,
    billCurrencyCode: "CAD",
    payCurrencyCode: "CAD",
    templateV2Id: null,
    dateCreated: "2026-02-12",
    status: "Pending",
    atsSource: {
      workerName: "Taylor Novak",
      workerEmail: "taylor.novak@example.com",
      clientOrgName: "Contoso Health",
      internalCompanyName: "Procom Canada",
      profileTypeCode: "WorkerCanadianInc",
      workerOrgName: "Novak Engineering Ltd.",
      lobCode: "Regular",
      startDate: "2026-08-01",
      endDate: "2027-01-31",
      billRate: 135,
      payRate: 95,
      billCurrencyCode: "CAD",
      payCurrencyCode: "CAD",
    },
  },
  {
    // Edge case: ATS client not in our system -- no mapped client org
    id: "ATS-10011",
    atsId: "ATS-10011",
    internalCompanyId: "ic-001",
    clientOrgId: null,
    workerId: "wk-005",
    workerContactId: null,
    workerOrgId: null,
    workerEmail: "priya.nair@example.com",
    lobCode: "Payroll",
    profileTypeCode: "WorkerCanadianSP",
    startDate: "2026-04-01",
    endDate: "2026-09-30",
    billRate: 72,
    payRate: 48,
    billCurrencyCode: null,
    payCurrencyCode: null,
    templateV2Id: null,
    dateCreated: "2026-02-03",
    status: "Pending",
    atsSource: {
      workerName: "Priya Nair",
      workerEmail: "priya.nair@example.com",
      clientOrgName: "Acme Corp International",
      internalCompanyName: "Procom Canada",
      profileTypeCode: "WorkerCanadianSP",
      workerOrgName: null,
      lobCode: "Payroll",
      startDate: "2026-04-01",
      endDate: "2026-09-30",
      billRate: 72,
      payRate: 48,
      billCurrencyCode: null,
      payCurrencyCode: null,
    },
  },
  {
    // Blank slate: no ATS values mapped -- user must fill in everything manually
    id: "ATS-99999",
    atsId: "ATS-99999",
    internalCompanyId: null,
    clientOrgId: null,
    workerId: null,
    workerContactId: null,
    workerOrgId: null,
    workerEmail: "",
    lobCode: null,
    profileTypeCode: null,
    startDate: "",
    endDate: "",
    billRate: null,
    payRate: null,
    billCurrencyCode: null,
    payCurrencyCode: null,
    templateV2Id: null,
    dateCreated: "2026-02-27",
    status: "Pending",
    atsSource: {
      workerName: "",
      workerEmail: "",
      clientOrgName: "",
      internalCompanyName: null,
      profileTypeCode: null,
      workerOrgName: null,
      lobCode: null,
      startDate: "",
      endDate: "",
      billRate: null,
      payRate: null,
      billCurrencyCode: null,
      payCurrencyCode: null,
    },
  },
]

// Copies of ATS-10001 with a linked pre-verification, one per way the field renders.
const preVerificationMockBase = initialPlacements.find((p) => p.atsId === "ATS-10001")!
initialPlacements.push(
  {
    ...preVerificationMockBase,
    id: "ATS-10016",
    atsId: "ATS-10016",
    preVerification: { requestId: "mock-pv-review", phase: "Submitted", allowedDecisions: [] },
  },
  {
    ...preVerificationMockBase,
    id: "ATS-10017",
    atsId: "ATS-10017",
    preVerification: { requestId: "mock-pv-worker", phase: "WithWorker", allowedDecisions: ["Discard"] },
  },
  {
    ...preVerificationMockBase,
    id: "ATS-10018",
    atsId: "ATS-10018",
    preVerification: { requestId: "mock-pv-approved", phase: "ApprovedSinceOpened", allowedDecisions: ["ContinueWithoutReview"] },
  },
  {
    ...preVerificationMockBase,
    id: "ATS-10019",
    atsId: "ATS-10019",
    preVerification: { requestId: "mock-pv-down", phase: "Unavailable", allowedDecisions: ["ContinueUnchecked"] },
  },
  {
    ...preVerificationMockBase,
    id: "ATS-10020",
    atsId: "ATS-10020",
    preVerification: {
      requestId: "mock-pv-done",
      phase: "Approved",
      allowedDecisions: [],
      finishedAt: "2026-10-12T15:30:00+00:00",
    },
  },
  {
    ...preVerificationMockBase,
    id: "ATS-10021",
    atsId: "ATS-10021",
    preVerification: {
      requestId: "mock-pv-discarded",
      phase: "Discarded",
      allowedDecisions: [],
      finishedAt: "2026-10-14T09:00:00+00:00",
    },
  },
  {
    ...preVerificationMockBase,
    id: "ATS-10022",
    atsId: "ATS-10022",
    preVerification: {
      requestId: "mock-pv-cancelled",
      phase: "Cancelled",
      allowedDecisions: [],
      finishedAt: "2026-10-15T09:00:00+00:00",
    },
  },
  {
    ...preVerificationMockBase,
    id: "ATS-10023",
    atsId: "ATS-10023",
    preVerification: { requestId: "mock-pv-not-sent", phase: "NotSent", allowedDecisions: ["Discard"] },
  },
  {
    ...preVerificationMockBase,
    id: "ATS-10024",
    atsId: "ATS-10024",
    preVerification: { requestId: "mock-pv-down-client-request", phase: "Unavailable", allowedDecisions: [] },
  },
)

// ---------------------------------------------------------------------------
// Lookup helpers (used by table / drawer)
// ---------------------------------------------------------------------------

export function resolveInternalCompany(id: string | null): string {
  if (!id) return "\u2014"
  return internalCompanies.find((c) => c.id === id)?.name ?? "\u2014"
}

export function resolveClientOrg(id: string | null): string {
  if (!id) return "\u2014"
  return clientOrganizations.find((c) => c.id === id)?.name ?? "\u2014"
}

export function resolveProfileTypeByCode(code: string | null): string {
  if (!code) return "\u2014"
  return profileTypes.find((p) => p.code === code)?.value ?? "\u2014"
}

export function resolveWorkerOrg(id: string | null): string {
  if (!id) return "\u2014"
  return workerOrganizations.find((o) => o.id === id)?.name ?? "\u2014"
}

export function resolveLobByCode(code: string | null): string {
  if (!code) return "\u2014"
  return lobs.find((l) => l.code === code)?.value ?? "\u2014"
}

export function resolveWorkerName(id: string | null): string {
  if (!id) return "\u2014"
  return workerProfiles.find((w) => w.id === id)?.name ?? "\u2014"
}

export function resolveWorkerEmail(id: string | null): string {
  if (!id) return ""
  return workerProfiles.find((w) => w.id === id)?.email ?? ""
}

/** Check if a placement is missing required mapping that blocks Start Onboarding.
 *  Uses DECISION_TABLES.BLOCKING rules as the single source of truth. */
export function isPlacementBlocked(p: Placement): { blocked: boolean; reasons: string[] } {
  const reasons: string[] = []
  if (DECISION_TABLES.BLOCKING.BLOCK_IF_NO_CLIENT_ORG && !p.clientOrgId)
    reasons.push("Client organization not mapped")
  if (DECISION_TABLES.BLOCKING.BLOCK_IF_NO_PROFILE_TYPE && !p.profileTypeCode)
    reasons.push("Profile type not set")
  // Status-based blocking via decision table blocklist
  if (p.workerId) {
    const worker = workerProfiles.find((w) => w.id === p.workerId)
    if (worker && isStatusBlockedFn(worker.contactStatus)) {
      reasons.push("Worker has pending contact changes")
    }
  }
  return { blocked: reasons.length > 0, reasons }
}

export const mockSourceConfig = {
  sourceName: "ATS queue",
  recordNoun: "placement"
}
